import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Navigation, Share2, MessageCircle } from 'lucide-react';

/**
 * BusinessFooterActions - Native app-style sticky bottom bar with primary actions
 */
export function BusinessFooterActions({
    phoneNumber,
    whatsappAvailable,
    onCall,
    onMessage,
    onDirections,
    onShare,
    showOnMobile = true,
    showOnDesktop = true
}) {
    const actions = [
        phoneNumber && {
            icon: Phone,
            label: 'התקשר',
            onClick: onCall,
            color: 'text-brand-500'
        },
        whatsappAvailable && phoneNumber && {
            icon: MessageCircle,
            label: 'הודעה',
            onClick: onMessage,
            color: 'text-success-500'
        },
        onDirections && {
            icon: Navigation,
            label: 'ניווט',
            onClick: onDirections,
            color: 'text-secondary-500'
        },
        onShare && {
            icon: Share2,
            label: 'שתף',
            onClick: onShare,
            color: 'text-ink-muted'
        }
    ].filter(Boolean);

    if (actions.length === 0) return null;

    return (
        <>
            {/* Mobile: Sticky Bottom Bar */}
            {showOnMobile && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                    className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-line shadow-md z-40"
                >
                    <div className="grid grid-cols-4 gap-0 divide-x divide-line" style={{ direction: 'ltr' }}>
                        {actions.map((action, idx) => {
                            const Icon = action.icon;
                            return (
                                <button
                                    key={idx}
                                    onClick={action.onClick}
                                    className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 hover:bg-surface-muted active:bg-surface-muted transition-colors"
                                >
                                    <Icon className={`w-5 h-5 ${action.color}`} strokeWidth={1.5} />
                                    <span className="text-xs font-semibold text-ink">
                                        {action.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </motion.div>
            )}

            {/* Desktop: Inline Actions */}
            {showOnDesktop && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="hidden md:flex px-6 py-4 bg-white border-t border-line gap-3"
                >
                    {actions.map((action, idx) => {
                        const Icon = action.icon;
                        return (
                            <motion.button
                                key={idx}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={action.onClick}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-ink-strong hover:bg-ink-strong text-white font-semibold text-sm shadow-card hover:shadow-md transition-all"
                            >
                                <Icon className="w-4 h-4" strokeWidth={2} />
                                <span>{action.label}</span>
                            </motion.button>
                        );
                    })}
                </motion.div>
            )}
        </>
    );
}

export default BusinessFooterActions;
